import React, { useState, useEffect } from "react";
import Select from "react-select";
import { message, Spin } from "antd";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import {
  useGetLocalizationSettingsQuery,
  useSaveLocalizationSettingsMutation,
  useGetCountriesQuery,
} from "../../../../services/settings.service";
import { localizationSettingsSchema } from "../../../../utils/validationSchemas";
import { useFormValidation } from "../../../../hooks/useFormValidation";

const Localization = () => {
  const [formData, setFormData] = useState({
    defaultCountryId: null,
    dateFormat: "DD/MM/YYYY",
    timezone: "UTC",
    defaultLanguage: "en",
    currencyCode: "USD",
    currencySymbol: "$",
  });

  // Validation Hook
  const { validateForm } = useFormValidation(localizationSettingsSchema);

  // API Hooks
  const { data: localizationData, isLoading } = useGetLocalizationSettingsQuery();
  const { data: countriesData } = useGetCountriesQuery();
  const [saveSettings, { isLoading: isSaving }] = useSaveLocalizationSettingsMutation();

  const countries = countriesData?.data || [];

  // Load initial data
  useEffect(() => {
    if (localizationData?.data) {
      const data = localizationData.data;
      setFormData({
        defaultCountryId: data.defaultCountryId || null,
        dateFormat: data.dateFormat || "DD/MM/YYYY",
        timezone: data.timezone || "UTC",
        defaultLanguage: data.defaultLanguage || "en",
        currencyCode: data.currencyCode || "USD",
        currencySymbol: data.currencySymbol || "$",
      });
    }
  }, [localizationData]);

  const dateFormatOptions = [
    { label: "DD/MM/YYYY (15/05/2023)", value: "DD/MM/YYYY" },
    { label: "DD.MM.YYYY (15.05.2023)", value: "DD.MM.YYYY" },
    { label: "DD-MM-YYYY (15-05-2023)", value: "DD-MM-YYYY" },
    { label: "MM/DD/YYYY (05/15/2023)", value: "MM/DD/YYYY" },
    { label: "YYYY/MM/DD (2023/05/15)", value: "YYYY/MM/DD" },
    { label: "YYYY-MM-DD (2023-05-15)", value: "YYYY-MM-DD" },
    { label: "MMMM DD, YYYY (May 15, 2023)", value: "MMMM DD, YYYY" },
    { label: "DD MMMM YYYY (15 May 2023)", value: "DD MMMM YYYY" },
  ];

  const timezoneOptions = [
    { label: "(UTC-12:00) International Date Line West", value: "Etc/GMT+12" },
    { label: "(UTC-11:00) Coordinated Universal Time-11", value: "Etc/GMT+11" },
    { label: "(UTC-10:00) Hawaii", value: "Pacific/Honolulu" },
    { label: "(UTC-09:00) Alaska", value: "America/Anchorage" },
    { label: "(UTC-08:00) Pacific Time (US & Canada)", value: "America/Los_Angeles" },
    { label: "(UTC-07:00) Mountain Time (US & Canada)", value: "America/Denver" },
    { label: "(UTC-06:00) Central Time (US & Canada)", value: "America/Chicago" },
    { label: "(UTC-05:00) Eastern Time (US & Canada)", value: "America/New_York" },
    { label: "(UTC-04:00) Atlantic Time (Canada)", value: "America/Halifax" },
    { label: "(UTC-03:00) Brasilia", value: "America/Sao_Paulo" },
    { label: "(UTC-02:00) Mid-Atlantic", value: "Etc/GMT+2" },
    { label: "(UTC-01:00) Azores", value: "Atlantic/Azores" },
    { label: "(UTC+00:00) London, Dublin, Lisbon", value: "Europe/London" },
    { label: "(UTC+01:00) Paris, Brussels, Copenhagen", value: "Europe/Paris" },
    { label: "(UTC+02:00) Athens, Istanbul, Jerusalem", value: "Europe/Athens" },
    { label: "(UTC+03:00) Moscow, Baghdad, Nairobi", value: "Europe/Moscow" },
    { label: "(UTC+04:00) Dubai, Baku", value: "Asia/Dubai" },
    { label: "(UTC+05:00) Islamabad, Karachi, Tashkent", value: "Asia/Karachi" },
    { label: "(UTC+05:30) Mumbai, Kolkata, Chennai, New Delhi", value: "Asia/Kolkata" },
    { label: "(UTC+06:00) Dhaka, Astana", value: "Asia/Dhaka" },
    { label: "(UTC+07:00) Bangkok, Jakarta, Hanoi", value: "Asia/Bangkok" },
    { label: "(UTC+08:00) Beijing, Hong Kong, Singapore", value: "Asia/Shanghai" },
    { label: "(UTC+09:00) Tokyo, Seoul, Osaka", value: "Asia/Tokyo" },
    { label: "(UTC+10:00) Sydney, Melbourne, Brisbane", value: "Australia/Sydney" },
    { label: "(UTC+11:00) Solomon Is., New Caledonia", value: "Pacific/Guadalcanal" },
    { label: "(UTC+12:00) Auckland, Wellington, Fiji", value: "Pacific/Auckland" },
  ];

  const languageOptions = [
    { label: "English", value: "en" },
    { label: "French", value: "fr" },
    { label: "Spanish", value: "es" },
    { label: "German", value: "de" },
    { label: "Italian", value: "it" },
    { label: "Portuguese", value: "pt" },
    { label: "Chinese", value: "zh" },
    { label: "Japanese", value: "ja" },
    { label: "Arabic", value: "ar" },
  ];

  const currencyOptions = [
    { label: "USD - US Dollar", value: "USD", symbol: "$" },
    { label: "EUR - Euro", value: "EUR", symbol: "€" },
    { label: "GBP - British Pound", value: "GBP", symbol: "£" },
    { label: "JPY - Japanese Yen", value: "JPY", symbol: "¥" },
    { label: "AUD - Australian Dollar", value: "AUD", symbol: "A$" },
    { label: "CAD - Canadian Dollar", value: "CAD", symbol: "C$" },
    { label: "CHF - Swiss Franc", value: "CHF", symbol: "Fr" },
    { label: "CNY - Chinese Yuan", value: "CNY", symbol: "¥" },
    { label: "INR - Indian Rupee", value: "INR", symbol: "₹" },
    { label: "MYR - Malaysian Ringgit", value: "MYR", symbol: "RM" },
  ];

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#008a42" : "#fff",
      color: state.isFocused ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#008a42",
      },
    }),
  };

  // Convert countries to select options
  const countryOptions = countries.map(country => ({
    label: country.name,
    value: country.id
  }));

  // Handle country change
  const handleCountryChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      defaultCountryId: selectedOption?.value || null
    }));
  };

  // Handle date format change
  const handleDateFormatChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      dateFormat: selectedOption?.value || "DD/MM/YYYY"
    }));
  };

  // Handle timezone change
  const handleTimezoneChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      timezone: selectedOption?.value || "UTC"
    }));
  };

  // Handle language change
  const handleLanguageChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      defaultLanguage: selectedOption?.value || "en"
    }));
  };

  // Handle currency change
  const handleCurrencyChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      currencyCode: selectedOption?.value || "USD",
      currencySymbol: selectedOption?.symbol || "$"
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate entire form
    const { isValid } = await validateForm(formData);

    if (!isValid) {
      message.error('Please fill in all required fields correctly');
      return;
    }

    try {
      // If validation passes, save settings
      await saveSettings(formData).unwrap();
      message.success('Localization settings saved successfully');
    } catch (error) {
      // Handle API errors
      message.error(error?.data?.message || 'Failed to save localization settings');
    }
  };

  // Get selected values
  const selectedCountry = countryOptions.find(opt => opt.value === formData.defaultCountryId);
  const selectedDateFormat = dateFormatOptions.find(opt => opt.value === formData.dateFormat);
  const selectedTimezone = timezoneOptions.find(opt => opt.value === formData.timezone);
  const selectedLanguage = languageOptions.find(opt => opt.value === formData.defaultLanguage);
  const selectedCurrency = currencyOptions.find(opt => opt.value === formData.currencyCode);

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        {/* Page Content */}
        <div className="content container-fluid">
          <div className="row">
            <div className="col-md-8 offset-md-2 box-align">
              {/* Page Header */}
              <Breadcrumbs maintitle="Localization Settings" />
              {/* /Page Header */}
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Default Country</label>
                      <Select
                        options={countryOptions}
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        placeholder="Select Country"
                        styles={customStyles}
                        isClearable
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Date Format</label>
                      <Select
                        options={dateFormatOptions}
                        value={selectedDateFormat}
                        onChange={handleDateFormatChange}
                        placeholder="Select Date Format"
                        styles={customStyles}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Timezone</label>
                      <Select
                        options={timezoneOptions}
                        value={selectedTimezone}
                        onChange={handleTimezoneChange}
                        placeholder="Select Timezone"
                        styles={customStyles}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Default Language</label>
                      <Select
                        options={languageOptions}
                        value={selectedLanguage}
                        onChange={handleLanguageChange}
                        placeholder="Select Language"
                        styles={customStyles}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Currency Code</label>
                      <Select
                        options={currencyOptions}
                        value={selectedCurrency}
                        onChange={handleCurrencyChange}
                        placeholder="Select Currency"
                        styles={customStyles}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Currency Symbol</label>
                      <input
                        className="form-control"
                        readOnly
                        value={formData.currencySymbol}
                        type="text"
                      />
                    </div>
                  </div>
                  <div className="col-sm-12">
                    <div className="submit-section">
                      <button
                        className="btn btn-primary submit-btn"
                        type="submit"
                        disabled={isSaving}
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Page Content */}
      </div>
    </>
  );
};

export default Localization;
